import React, { useState, useEffect } from "react";
import { Bell, Search, User, RefreshCw } from "lucide-react";
import { auth, db } from "../../Services/Firebase/FirebaseIndex";
import { doc, getDoc } from "firebase/firestore";

const AdminHeader = ({ usingCachedData = false, onRefresh }) => {
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminRole, setAdminRole] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get current admin info
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentAdmin(user);
        
        // Fetch admin role information
        try {
          const adminRef = doc(db, "AdminUsers", user.uid);
          const adminDoc = await getDoc(adminRef);
          
          if (adminDoc.exists()) {
            // Get admin details
            const adminData = adminDoc.data();
            if (adminData.isSuperAdmin) {
              setAdminRole("Super Administrator");
            } else if (adminData.roleId) {
              // Fetch role details
              const roleRef = doc(db, "AdminRoles", adminData.roleId);
              const roleDoc = await getDoc(roleRef);
              
              if (roleDoc.exists()) {
                setAdminRole(roleDoc.data().name);
              } else {
                setAdminRole("Administrator");
              }
            } else {
              setAdminRole("Administrator");
            }
          } else {
            setAdminRole("Unknown Role");
          }
        } catch (error) {
          console.error("Error fetching admin role:", error);
          setAdminRole("Administrator");
        }
      } else {
        setCurrentAdmin(null);
        setAdminRole(null);
      }
      
      setLoading(false);
    });

    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, []);

  return (
    <header style={styles.header}>
      <div style={styles.titleSection}>
        <h1 style={styles.title}>Admin Dashboard</h1>
        <div style={styles.dateTime}>
          {currentTime.toLocaleDateString("en-GB", {
            weekday: "long", 
            year: "numeric", 
            month: "long", 
            day: "numeric"
          })}
          {" "}
          {currentTime.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit"
          })}
        </div>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchBox}>
          <Search size={20} style={{ color: "#144874" }} />
          <input
            type="text"
            placeholder="Search for users, bookings..."
            style={styles.searchInput}
          />
        </div>

        {/* Compact cached data notification */}
        {usingCachedData && (
          <div style={styles.cachedDataNotification}>
            <span style={styles.cachedDataText}>Cached Data</span>
            <RefreshCw
              size={16}
              style={styles.refreshIcon}
              onClick={onRefresh}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(90deg)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(0deg)'}
            />
          </div>
        )}
      </div>

      <div style={styles.userSection}>
        <div style={styles.notificationIcon}>
          <Bell size={24} />
          <span style={styles.notificationBadge}>3</span>
        </div>
        <div style={styles.userInfo}>
          <div style={styles.userAvatar}>
            <User size={24} />
          </div>
          <div style={styles.userDetails}>
            <p style={styles.userName}>
              {loading
                ? "Loading..."
                : currentAdmin?.displayName || 
                  (currentAdmin ? `${currentAdmin.email.split('@')[0]}` : "Admin User")}
            </p>
            <p style={styles.userRole}>
              {adminRole || "Administrator"}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
};

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
    padding: "15px 30px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    position: "fixed",
    top: 0,
    right: 0,
    left: "280px", // Same as sidebar width
    height: "80px",
    zIndex: 99,
  },
  titleSection: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: 0,
    color: "#144874",
  },
  dateTime: {
    color: "#666",
    fontSize: "14px",
    marginTop: "5px",
  },
  searchContainer: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "12px",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    width: "400px",
    padding: "10px 15px",
    backgroundColor: "#f5f5f5",
    borderRadius: "20px",
    border: "1px solid #eee",
    transition: "all 0.3s ease"
  },
  cachedDataNotification: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "6px 12px",
    borderRadius: "15px",
    fontSize: "12px",
    fontWeight: "500",
  },
  cachedDataText: {
    whiteSpace: "nowrap",
  },
  refreshIcon: {
    color: "#856404",
    cursor: "pointer",
    transition: "transform 0.3s ease",
  },
  searchInput: {
    border: "none",
    backgroundColor: "transparent",
    marginLeft: "10px",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    color: "#333"
  },
  userSection: {
    display: "flex",
    alignItems: "center",
  },
  notificationIcon: {
    position: "relative",
    marginRight: "20px",
    cursor: "pointer",
  },
  notificationBadge: {
    position: "absolute",
    top: "-5px",
    right: "-5px",
    backgroundColor: "#f85008",
    color: "white",
    borderRadius: "50%",
    width: "18px",
    height: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: "bold",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
  },
  userAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#01a2f1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    marginRight: "10px",
  },
  userDetails: {
    textAlign: "left",
  },
  userName: {
    margin: 0,
    fontWeight: "bold",
    fontSize: "14px",
    color: "#333",
  },
  userRole: {
    margin: 0,
    fontSize: "12px",
    color: "#666",
  },
};

export default AdminHeader;